import _classCallCheck from "/codebuild/output/src4243671113/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "/codebuild/output/src4243671113/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/createClass";
import _inherits from "/codebuild/output/src4243671113/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/inherits";
import _createSuper from "/codebuild/output/src4243671113/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/createSuper";
/**
 * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */
/**
 * @module widget/widgetresize/resizerstate
 */
import { ObservableMixin, Rect } from '@ckeditor/ckeditor5-utils';
/**
 * Stores the internal state of a single resizable object.
 */
var ResizeState = /*#__PURE__*/function (_ObservableMixin) {
  _inherits(ResizeState, _ObservableMixin);
  var _super = _createSuper(ResizeState);
  /**
   * @param options Resizer options.
   */
  function ResizeState(options) {
    var _this;
    _classCallCheck(this, ResizeState);
    _this = _super.call(this);
    _this.set('activeHandlePosition', null);
    _this.set('proposedWidthPercents', null);
    _this.set('proposedWidth', null);
    _this.set('proposedHeight', null);
    _this.set('proposedHandleHostWidth', null);
    _this.set('proposedHandleHostHeight', null);
    _this._options = options;
    _this._referenceCoordinates = null;
    return _this;
  }
  /**
   * The original width (pixels) of the resized object when the resize process was started.
   */
  _createClass(ResizeState, [{
    key: "originalWidth",
    get: function get() {
      return this._originalWidth;
    }
    /**
     * The original height (pixels) of the resized object when the resize process was started.
     */
  }, {
    key: "originalHeight",
    get: function get() {
      return this._originalHeight;
    }
    /**
     * The original width (percents) of the resized object when the resize process was started.
     */
  }, {
    key: "originalWidthPercents",
    get: function get() {
      return this._originalWidthPercents;
    }
    /**
     * A width to height ratio of the resized image.
     */
  }, {
    key: "aspectRatio",
    get: function get() {
      return this._aspectRatio;
    }
    /**
     *
     * @param domResizeHandle The handle used to calculate the reference point.
     */
  }, {
    key: "begin",
    value: function begin(domResizeHandle, domHandleHost, domResizeHost) {
      var clientRect = new Rect(domHandleHost);
      this.activeHandlePosition = getHandlePosition(domResizeHandle);
      this._referenceCoordinates = getAbsoluteBoundaryPoint(domHandleHost, getOppositePosition(this.activeHandlePosition));
      this._originalWidth = clientRect.width;
      this._originalHeight = clientRect.height;
      this._aspectRatio = clientRect.width / clientRect.height;
      var widthStyle = domResizeHost.style.width;
      if (widthStyle && widthStyle.match(/^\d+(\.\d*)?%$/)) {
        this._originalWidthPercents = parseFloat(widthStyle);
      } else {
        this._originalWidthPercents = calculateHostPercentageWidth(domResizeHost, clientRect);
      }
    }
  }, {
    key: "update",
    value: function update(newSize) {
      this.proposedWidth = newSize.width;
      this.proposedHeight = newSize.height;
      this.proposedWidthPercents = newSize.widthPercents;
      this.proposedHandleHostWidth = newSize.handleHostWidth;
      this.proposedHandleHostHeight = newSize.handleHostHeight;
    }
  }]);
  return ResizeState;
}(ObservableMixin());
/**
 * Calculates a relative width of a `domResizeHost` compared to its ancestor in percents.
 */
export { ResizeState as default };
function calculateHostPercentageWidth(domResizeHost, resizeHostRect) {
  var domResizeHostParent = domResizeHost.parentElement;
  // Need to use computed style as it properly excludes parent's paddings from the returned value.
  var parentWidth = parseFloat(domResizeHostParent.ownerDocument.defaultView.getComputedStyle(domResizeHostParent).width);
  // Sometimes parent width cannot be accessed. If that happens we should go up in the elements tree
  // and try to get width from next ancestor.
  // https://github.com/ckeditor/ckeditor5/issues/10776
  var ancestorLevelLimit = 5;
  var currentLevel = 0;
  var checkedElement = domResizeHostParent;
  while (isNaN(parentWidth)) {
    checkedElement = checkedElement.parentElement;
    if (++currentLevel > ancestorLevelLimit) {
      return 0;
    }
    parentWidth = parseFloat(domResizeHostParent.ownerDocument.defaultView.getComputedStyle(checkedElement).width);
  }
  return resizeHostRect.width / parentWidth * 100;
}
/**
 * Returns coordinates of the top-left corner of an element, relative to the document's top-left corner.
 *
 * @param resizerPosition The position of the resize handle, e.g. `"top-left"`, `"bottom-right"`.
 */
function getAbsoluteBoundaryPoint(element, resizerPosition) {
  var elementRect = new Rect(element);
  var positionParts = resizerPosition.split('-');
  var ret = {
    x: positionParts[1] == 'right' ? elementRect.right : elementRect.left,
    y: positionParts[0] == 'bottom' ? elementRect.bottom : elementRect.top
  };
  ret.x += element.ownerDocument.defaultView.scrollX;
  ret.y += element.ownerDocument.defaultView.scrollY;
  return ret;
}
/**
 * @param resizerPosition The expected resizer position, like `"top-left"`, `"bottom-right"`.
 * @returns A prefixed HTML class name for the resizer element.
 */
function getResizerHandleClass(resizerPosition) {
  return "ck-widget__resizer__handle-".concat(resizerPosition);
}
/**
 * Determines the position of a given resize handle.
 *
 * @param domHandle Handle used to calculate the reference point.
 * @returns Returns a string like `"top-left"` or `undefined` if not matched.
 */
function getHandlePosition(domHandle) {
  var resizerPositions = ['top-left', 'top-right', 'bottom-right', 'bottom-left'];
  for (var _i = 0, _resizerPositions = resizerPositions; _i < _resizerPositions.length; _i++) {
    var position = _resizerPositions[_i];
    if (domHandle.classList.contains(getResizerHandleClass(position))) {
      return position;
    }
  }
}
/**
 * @param position Like `"top-left"`.
 * @returns Inverted `position`, e.g. it returns `"bottom-right"` if `"top-left"` was given as `position`.
 */
function getOppositePosition(position) {
  var parts = position.split('-');
  var replacements = {
    top: 'bottom',
    bottom: 'top',
    left: 'right',
    right: 'left'
  };
  return "".concat(replacements[parts[0]], "-").concat(replacements[parts[1]]);
}